import { UnsavedChanges } from "@/utils/UnsavedChanges.util.ts";
import { AbstractHasBufferedUpdates } from "@/models/AbstractHasBufferedUpdates.model.ts";
import { notify } from 'notiwind'

export interface BulkActionsStore<T extends AbstractHasBufferedUpdates> {
	bulkSave(items: T[]): Promise<T[]>;
	bulkDelete(items: T[]): Promise<void>;

	updateOrAddStoreItem(item: T): T;
	removeStoreItem(item: T): T;
}

export class CollectionChangeBuffer<T extends AbstractHasBufferedUpdates> {

	private createBuffer: T[] = [];
	private deleteBuffer: T[] = []; // The delete buffer contains clones of the items to be deleted
	private deleteBufferOriginalReference: T[] = []; // As we pre-delete items, we keep a copy of the original items to restore them if needed

	private store: BulkActionsStore<T>;
	private bufferTime: number; // ms
	private updateTimeout: any = null;
	private isUpdating: boolean = false;

	constructor(
		store: BulkActionsStore<T>,
		bufferTime = 1000
	) {
		this.store = store;
		this.bufferTime = bufferTime;
	}

	addToCreateBuffer(item: T) {
		// Re-adding an item that is still waiting to be deleted: cancel the pending
		// delete and put the (optimistically removed) item back in the store.
		const deleteIndex = this.deleteBufferOriginalReference.indexOf(item);
		if (deleteIndex !== -1) {
			this.deleteBuffer.splice(deleteIndex, 1);
			this.deleteBufferOriginalReference.splice(deleteIndex, 1);
			this.store.updateOrAddStoreItem(item);
			this.unmarkPendingSync(item);
			return;
		}

		this.createBuffer.push(item);
		item = this.store.updateOrAddStoreItem(item);

		item.pendingSync = true;

		if (!this.isUpdating) {
			this.startUpdateTimer();
		}
	}

	addToDeleteBuffer(item: T) {

		// Deleting an item that is still waiting to be created: cancel the pending
		// create and take the (optimistically added) item out of the store again.
		if (this.createBuffer.includes(item)) {
			this.createBuffer.splice(this.createBuffer.indexOf(item), 1);
			this.store.removeStoreItem(item);
			this.unmarkPendingSync(item);
			return;
		}
		this.deleteBuffer.push(item.clone());
		this.deleteBufferOriginalReference.push(item);

		this.store.removeStoreItem(item);

		item.pendingSync = true;

		if (!this.isUpdating) {
			this.startUpdateTimer();
		}
	}

	startUpdateTimer() {

		UnsavedChanges.markUnsavedChanges();

		if (this.updateTimeout) {
			clearTimeout(this.updateTimeout);
		}

		this.updateTimeout = setTimeout(() => {
			this.handleUpdate();
		}, this.bufferTime);
	}

	async handleUpdate() {
		if (!this.isUpdating && (this.createBuffer.length > 0 || this.deleteBuffer.length > 0)) {
			this.isUpdating = true;

			// Perform the bulk update
			const itemsToImport = [...this.createBuffer];
			this.createBuffer = [];

			const itemsToDelete = [...this.deleteBuffer];
			const itemsToDeleteOriginalReference = [...this.deleteBufferOriginalReference];

			this.deleteBuffer = [];
			this.deleteBufferOriginalReference = [];

			try {
				if (itemsToImport.length > 0) {
					(await this.store.bulkSave(itemsToImport)).forEach(
						(item: T) => {
							this.unmarkPendingSync(item);
						}
					);

					// Also mark all original queued items as done, as these might be other references.
					itemsToImport.forEach(
						(item: T) => {
							this.unmarkPendingSync(item);
						}
					);
				}
			} catch (e) {

				notify({
					title: "Error!",
					text: '' + e,
					type: "error",
				});

				// @Todo: rollback all changes
				throw e;

			}

			try {
				if (itemsToDelete.length > 0) {
					await this.store.bulkDelete(itemsToDelete);

					itemsToDeleteOriginalReference.forEach(
						(item: T) => {
							this.unmarkPendingSync(item);
						}
					)
				}
			} catch (e) {

				notify({
					title: "Error!",
					text: '' + e,
					type: "error",
				});

				// @Todo: rollback all changes
				throw e;

			}

			this.isUpdating = false;
			UnsavedChanges.markSavedChanges();

			// If new changes occurred during the previous update, trigger another update
			if (this.createBuffer.length > 0 || this.deleteBuffer.length > 0) {
				this.startUpdateTimer();
			}
		}
	}

	unmarkPendingSync(item: T) {
		if (!item.pendingSync) {
			return;
		}

		item.pendingSync = false;

		// After 2 seconds, set pendingSync to null
		setTimeout(() => {
			if (item.pendingSync === false) {
				item.pendingSync = null;
			}
		}, 2000);
	}
}
